import React, { useContext, useEffect, useRef, useState } from "react";
import "./GlobalSettings.css";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faBell, faBuilding, faFileUpload } from "@fortawesome/free-solid-svg-icons";

//Context
import authContext from "../../context/authentication/authContex";

//Api
import { createGlobalSettings } from "../../api/globalSettings";

const GlobalSettings = () => {
  const { globalSettings, obtenerGlobalSettings, updateGlobalSettings } =
    useContext(authContext);

  const [loading, setLoading] = useState(true);
  const [updated, setUpdated] = useState(null);
  const [sideNavBarHovered, setSideNavBarHovered] = useState(false);
  const [hoveringImgContainer, setHoveringImgContainer] = useState(false);
  const [companyImg, setCompanyImg] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [saving, setSaving] = useState(false);

  const notificationsSectionRef = useRef(null);
  const companySectionRef = useRef(null);
  const fileInputRef = useRef(null);
  const actionOverImg = useRef("");

  useEffect(() => {
    const fn = async () => {
      try {
        const settings = await obtenerGlobalSettings();
        // Si no existen global settings, crearlos
        if (!settings) {
          await createGlobalSettings();
          await obtenerGlobalSettings();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fn();
  }, []);

  useEffect(() => {
    if (!globalSettings) return;
    setPreviewURL(globalSettings.companyImgURL || null);
    setUpdated({ ...globalSettings });
  }, [globalSettings]);

  useEffect(() => {
    return () => {
      if (previewURL?.startsWith("blob:")) {
        URL.revokeObjectURL(previewURL);
      }
    };
  }, [previewURL]);

  const handleChangeGlobalSettings = (e) => {
    const { name, value } = e.target;
    setUpdated((prevUpd) => ({
      ...prevUpd,
      [name]: value,
    }));
  };

  const handleScroll = (scrollingTo) => {
    switch (scrollingTo) {
      case "notifications":
        notificationsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case "company":
        companySectionRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      default:
        break;
    }
  };

  const handleSearchImgCompany = () => {
    fileInputRef.current?.click();
  };

  const handleDragOverImgContainer = (e) => {
    e.preventDefault();
    setHoveringImgContainer(true);
  };

  const handleDragLeaveImgContainer = (e) => {
    e.preventDefault();
    setHoveringImgContainer(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    actionOverImg.current = "CHANGED";
    if (file) {
      const fileType = file.type;
      const originalName = file.name;
      let newFile = file;
      if (fileType === "image/jpeg" || fileType === "image/png") {
        if (originalName.toLowerCase().endsWith(".jpeg")) {
          const newName = originalName.replace(/\.jpeg$/i, ".jpg");
          newFile = new File([file], newName, { type: file.type });
        }
        setCompanyImg(newFile);
        const objectURL = URL.createObjectURL(newFile);
        setPreviewURL(objectURL);
      } else {
        Swal.fire({
          title: "Ops!",
          text: "Solo se permiten imagenes en formato .jpg o .png",
          icon: "error",
        });
      }
    }
  };

  const handleDropFile = (e) => {
    e.preventDefault();
    setHoveringImgContainer(false);
    const file = e.dataTransfer.files[0];
    actionOverImg.current = "CHANGED";
    if (file) {
      const fileType = file.type;
      const originalName = file.name;
      let newFile = file;
      if (fileType === "image/jpeg" || fileType === "image/png") {
        if (originalName.toLowerCase().endsWith(".jpeg")) {
          const newName = originalName.replace(/\.jpeg$/i, ".jpg");
          newFile = new File([file], newName, { type: file.type });
        }
        setCompanyImg(newFile);
        const objectURL = URL.createObjectURL(newFile);
        setPreviewURL(objectURL);
      } else {
        Swal.fire({
          title: "Ops!",
          text: "Solo se permiten imagenes en formato .jpg o .png",
          icon: "error",
        });
      }
    }
  };

  const handleDeleteFile = (e) => {
    e.preventDefault();
    if (globalSettings.companyImgURL) actionOverImg.current = "DELETED";
    else actionOverImg.current = "";
    setPreviewURL(null);
    setCompanyImg(null);
  };

  const handleSave = async () => {
    setSaving(true);

    Swal.fire({
      title: "Aguarde a que se apliquen los cambios...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      let res = await updateGlobalSettings(
        { ...updated, previewURL },
        companyImg,
        actionOverImg.current
      );
      if (res) {
        setSaving(false);
        actionOverImg.current = "";
        Swal.fire({
          title: "Configuracion global actualizada exitosamente!",
          icon: "success",
        });
      } else {
        setSaving(false);
        Swal.fire({
          title: "Error al actualizar la configuracion",
          text: "La operacion no fue exitosa.",
          icon: "error",
        });
      }
    } catch (error) {
      setSaving(false);
      console.error("Error al guardar configuracion:", error);
      Swal.fire({
        title: "Error inesperado",
        text: "Ocurrio un error al intentar guardar la configuracion.",
        icon: "error",
      });
    }
  };

  if (loading) {
    return (
      <div className="globalSettingsLoading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="globalSettingsView">
      <div
        className="sideNavigationBar"
        onMouseEnter={() => setSideNavBarHovered(true)}
        onMouseLeave={() => setSideNavBarHovered(false)}
      >
        {!sideNavBarHovered && (
          <FontAwesomeIcon icon={faBars} className="icon" />
        )}
        <div className="sideNavBarContent">
          <h4 onClick={() => handleScroll("notifications")}>
            Notificaciones <FontAwesomeIcon icon={faBell} />
          </h4>
          <h4 onClick={() => handleScroll("company")}>
            Compañia <FontAwesomeIcon icon={faBuilding} />
          </h4>
        </div>
      </div>

      <div
        className="globalSettingsChildContainer"
        ref={notificationsSectionRef}
      >
        <h3>Notificaciones</h3>

        {updated && (
          <div className="openNewTabSetup">
            <div className="inputGroup">
              <div className="inputRow">
                <div>
                  <h5>Mails aviso producto sin precio en requerimiento:</h5>
                </div>
              </div>
              <p className="inputHint">
                Ingrese los mails separados por punto y coma (;)
              </p>
              <textarea
                id="mailsAvisoProductoSinPrecio"
                name="mailsAvisoProductoSinPrecio"
                className="mailsInput"
                onChange={handleChangeGlobalSettings}
                value={updated.mailsAvisoProductoSinPrecio || ""}
                placeholder="ejemplo@mail.com;otro@mail.com"
                rows={4}
              />
            </div>
          </div>
        )}

        <div className="companyInfo" ref={companySectionRef}>
          <h3>Compañia</h3>
          {updated && (
            <div className="inputGroup">
              <div
                style={{
                  width: "95%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div>
                  <h5>Nombre de la Empresa:</h5>
                </div>
                <div className="inputRequiredSubcuenta">
                  <input
                    className="companyNameInput"
                    name="companyName"
                    onChange={handleChangeGlobalSettings}
                    value={updated.companyName || ""}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="logoCompanyRow">
            <h5 className="logoCompanyLabel">Logo de la Empresa:</h5>
            {previewURL ? (
              <div className="previewCompanyImgContainer">
                <img
                  className="previewCompanyImg"
                  src={previewURL}
                  alt={`${updated?.companyName || "company"}-imgPreview`}
                />
                <button
                  className="buttonEliminarImagen"
                  onClick={handleDeleteFile}
                >
                  Eliminar Imagen
                </button>
              </div>
            ) : (
              <div className="uploadPhotoContainer">
                <div
                  className="dropPhotoContainer"
                  style={hoveringImgContainer ? { borderColor: "orange" } : {}}
                  onClick={handleSearchImgCompany}
                  onDrop={handleDropFile}
                  onDragOver={handleDragOverImgContainer}
                  onDragLeave={handleDragLeaveImgContainer}
                >
                  <label>
                    Suelte una imagen aqui o presione para buscarla en sus
                    archivos <FontAwesomeIcon icon={faFileUpload} />
                  </label>
                </div>
                <input
                  type="file"
                  accept=".jpg, .jpeg, .png"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <button disabled={saving} onClick={handleSave} className="saveBtn">
        Guardar
      </button>
    </div>
  );
};

export default GlobalSettings;
